/* eslint-disable max-lines */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import cn from 'classnames';
import { css, StyleSheet } from 'aphrodite';
import { useTranslation } from 'react-i18next';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';
import {
  Control,
  Controller,
  FieldErrors,
  UseFormSetValue
} from 'react-hook-form';
import { CustomerBo } from '@utils/types/back-office/customer-bo.ts';
import { useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { Popover, Transition } from '@headlessui/react';
import { BlockPicker } from 'react-color';
import { Eye, Image, Palette, Sparkles, Trash2, Upload } from 'lucide-react';
import Button from '@components/atoms/ui-kit/button.tsx';
import PageHeader from '@components/layout/page-header.tsx';
import ConfirmationModal from '@components/atoms/modals/confirmation-modal.tsx';
import { getSafeColorForElement } from '@utils/helpers/color-utils';
import { useDeleteCustomerLogo } from '@utils/api/back-office/customer-bo/use-delete-logo.tsx';
import { useUpdateCustomerLogo } from '@utils/api/back-office/customer-bo/use-patch-logo.tsx';

interface Props {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<CustomerBo, any>;
  errors: FieldErrors<CustomerBo>;
  setValue: UseFormSetValue<CustomerBo>;
  customer?: CustomerBo;
  customerId: string;
  cardStyle: React.CSSProperties;
}

export function CustomerBrandingTab({
  control,
  errors,
  setValue,
  customer,
  customerId,
  cardStyle
}: Props) {
  const { t } = useTranslation();
  const { getColour, theme } = useThemeContext();
  const queryClient = useQueryClient();

  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [deleteLogoIsOpen, setDeleteLogoIsOpen] = useState(false);
  const [brandColor, setBrandColor] = useState<string>('');
  const [brandColorAdjusted, setBrandColorAdjusted] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);

  const { mutate: deleteLogo, isLoading: isDeleting } = useDeleteCustomerLogo(
    Number(customerId)
  );
  const { mutate: updateLogo, isError } = useUpdateCustomerLogo(
    Number(customerId)
  );

  useEffect(() => {
    if (customer?.brand_color) {
      const safe = getSafeColorForElement(customer.brand_color, theme);
      setBrandColor(safe || '');
    }
  }, [customer, theme]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0] && customerId) {
      const file = event.target.files[0];
      setLogoFile(file);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      setValue('logo', file as any);
      updateLogo(file, {
        onSuccess: () => {
          toast.success(t('logo-updated-successfully'));
          void queryClient.invalidateQueries(['customer', Number(customerId)]);
          void queryClient.invalidateQueries(['customersBo']);
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onError: (error: any) => {
          const errorMessage =
            error?.response?.data?.error_description ||
            error?.response?.data?.detail ||
            error?.response?.data?.error ||
            error?.response?.data?.message ||
            t('failed-to-update-the-logo');
          toast.error(errorMessage);
        }
      });
    }
  };

  const handleDeleteLogo = () => {
    deleteLogo(undefined, {
      onSuccess: () => {
        toast.success(t('logo-deleted-successfully'));
        setLogoFile(null);
        setValue('logo', '');
        setDeleteLogoIsOpen(false);
        void queryClient.invalidateQueries(['customer', Number(customerId)]);
        void queryClient.invalidateQueries(['customersBo']);
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onError: (error: any) => {
        const errorMessage =
          error?.response?.data?.error_description ||
          error?.response?.data?.detail ||
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          t('failed-to-delete-the-logo');
        toast.error(errorMessage);
        setDeleteLogoIsOpen(false);
      }
    });
  };

  const pick = (light: string, dark: string) =>
    theme === 'dark' ? dark : light;
  /* eslint-disable no-restricted-syntax */
  const active = getColour('activeText') ?? '#2563eb';
  const text = getColour('normalText') ?? pick('#111827', '#f9fafb');
  const surface = getColour('appSoft') ?? pick('#ffffff', '#111827');
  const borderBase = getColour('hairline') ?? pick('#e5e7eb', '#4b5563');
  const borderHover = pick('#d1d5db', '#6b7280');
  const placeholderColor =
    getColour('inactiveText') ?? pick('#6b7280', '#9ca3af');
  /* eslint-enable no-restricted-syntax */
  const disabledBg = pick('rgba(17,24,39,0.03)', 'rgba(255,255,255,0.06)');
  const controlBg = theme === 'dark' ? 'transparent' : surface;

  const brandStyleVars: React.CSSProperties = {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ['--in-bg' as any]: surface,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ['--in-fg' as any]: text,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ['--in-ph' as any]: placeholderColor,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ['--in-border' as any]: borderBase,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ['--in-border-hover' as any]: borderHover,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ['--in-ring' as any]: active,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ['--in-disabled-bg' as any]: disabledBg,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ['--in-ctrl-bg' as any]: controlBg,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ['--in-menu-bg' as any]: surface
  };

  const styles = useMemo(
    () =>
      StyleSheet.create({
        mainContainerColor: { backgroundColor: getColour('appBackground') }
      }),
    [getColour]
  );

  return (
    <div className="space-y-4 py-4">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Logo Section */}
        <div
          className="rounded-2xl border shadow-card p-4 sm:p-6"
          style={cardStyle}
        >
          <PageHeader
            title={t('organization-logo')}
            caption={
              t('logo-description') ||
              'PNG, JPG or SVG. Recommended size: 200x200px'
            }
            className="border-b-0 mb-4"
            contentClassName="px-0 py-0"
          />

          <div
            className="relative rounded-2xl p-6 mb-4 flex items-center justify-center min-h-[120px]"
            style={{ backgroundColor: getColour('thirdLayer') }}
          >
            {customer?.logo || logoFile ? (
              <div className="relative group">
                <img
                  src={
                    logoFile
                      ? URL.createObjectURL(logoFile)
                      : customer?.logo || ''
                  }
                  alt="Organization logo"
                  className="max-h-20 max-w-full object-contain rounded-2xl"
                />
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-2xl flex items-center justify-center gap-2">
                  <label
                    htmlFor="logo-replace"
                    className="p-2 bg-white/20 rounded-2xl cursor-pointer hover:bg-white/30 transition-colors"
                  >
                    <Upload className="h-4 w-4 text-white" />
                    <input
                      id="logo-replace"
                      type="file"
                      accept="image/*"
                      className="sr-only"
                      onChange={handleFileChange}
                    />
                  </label>
                  <button
                    type="button"
                    onClick={handleDeleteLogo}
                    disabled={!customer?.logo}
                    className="p-2 bg-white/20 rounded-2xl hover:bg-red-500/80 transition-colors disabled:opacity-50"
                  >
                    <Trash2 className="h-4 w-4 text-white" />
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-center">
                <Image
                  className="mx-auto h-8 w-8 opacity-40 mb-2"
                  style={{ color: getColour('icon') }}
                />
                <p className="text-xs opacity-60">{t('no-logo-uploaded')}</p>
              </div>
            )}
          </div>

          <div className="flex gap-2 justify-center">
            <label htmlFor="logo">
              <Button
                label={
                  customer?.logo || logoFile
                    ? t('replace-logo')
                    : t('upload-logo')
                }
                icon={<Upload className="h-3.5 w-3.5" />}
                variant="secondary"
                size="xs"
                onClick={() => document.getElementById('logo')?.click()}
              />
              <input
                id="logo"
                name="logo"
                type="file"
                accept="image/*"
                className="sr-only"
                onChange={handleFileChange}
              />
            </label>
            {customer?.logo && (
              <Button
                label={t('remove-logo')}
                icon={<Trash2 className="h-3.5 w-3.5" />}
                variant="smoke"
                size="xs"
                onClick={() => setDeleteLogoIsOpen(true)}
              />
            )}
          </div>
          {errors.logo && (
            <p className="text-red-500 text-xs mt-2">{errors.logo.message}</p>
          )}
          {isError && (
            <p className="text-xs text-red-500 mt-2">
              {t('logo-update-error') ||
                'An error occurred while updating the logo.'}
            </p>
          )}
        </div>

        {/* Brand Color Section */}
        <div
          className="rounded-2xl border shadow-card p-4 sm:p-6"
          style={cardStyle}
        >
          <PageHeader
            title={t('brand-color')}
            caption={
              t('brand-color-description') ||
              'Primary color used for buttons and accents'
            }
            className="border-b-0 mb-4"
            contentClassName="px-0 py-0"
          />

          <div
            className="rounded-2xl p-4 mb-4 flex items-center justify-center min-h-[120px]"
            style={{ backgroundColor: getColour('thirdLayer') }}
          >
            <div className="flex items-center justify-center gap-4">
              <div
                className="w-16 h-16 rounded-xl shadow-inner flex items-center justify-center border"
                style={{
                  backgroundColor: brandColor || getColour('appSoft'),
                  borderColor: brandColor
                    ? 'transparent'
                    : getColour('hairline')
                }}
              >
                {!brandColor && (
                  <span className="text-xs opacity-40">{t('no-color')}</span>
                )}
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium mb-1">
                  {brandColor || t('no-color-selected')}
                </p>
                <p className="text-xs opacity-60">
                  {brandColor
                    ? t('click-to-change-color') || 'Click below to change'
                    : t('select-a-brand-color') || 'Select a brand color'}
                </p>
                {brandColorAdjusted && (
                  <div className="flex items-center gap-1 mt-1 text-xs text-amber-600">
                    <Sparkles className="h-3 w-3" />
                    <span>
                      {t('color-auto-adjusted') ||
                        'Auto-adjusted for readability'}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div style={brandStyleVars} className="flex justify-center">
            <Controller
              control={control}
              name="brand_color"
              defaultValue={brandColor || ''}
              render={() => (
                <Popover className="relative">
                  <Popover.Button
                    type="button"
                    id="brand_color"
                    aria-label={t('select-color') || 'Select color'}
                  >
                    <Button
                      label={brandColor ? t('change-color') : t('select-color')}
                      icon={<Palette className="h-3.5 w-3.5" />}
                      variant="secondary"
                      size="xs"
                    />
                  </Popover.Button>

                  <Transition
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                  >
                    <Popover.Panel
                      className={cn(
                        'absolute z-50 mt-2 left-0 w-80 max-w-[76vw] rounded-xl p-2 shadow-xl ring-1',
                        'bg-[var(--in-menu-bg)] text-[var(--in-fg)]',
                        'border border-[var(--in-border)] ring-[var(--in-border)]'
                      )}
                    >
                      <div ref={pickerRef}>
                        <div className="mb-3 flex items-center justify-between">
                          <span className="text-sm font-medium">
                            {t('pick-a-color')}
                          </span>
                          <button
                            type="button"
                            className="text-xs text-red-500 hover:text-red-600 hover:underline"
                            onClick={() => {
                              setBrandColor('');
                              setBrandColorAdjusted(false);
                              setValue('brand_color', '', {
                                shouldDirty: true
                              });
                            }}
                          >
                            {t('clear')}
                          </button>
                        </div>

                        <div
                          className={cn(
                            'rounded-2xl overflow-hidden w-full',
                            css(styles.mainContainerColor)
                          )}
                          style={{ borderRadius: '8px' }}
                        >
                          <BlockPicker
                            // eslint-disable-next-line no-restricted-syntax
                            color={brandColor || '#2584f5'}
                            triangle="hide"
                            width="100%"
                            styles={{
                              default: {
                                head: { height: '40px' },
                                body: { padding: '8px' },
                                input: { height: '30px', fontSize: '12px' }
                              }
                            }}
                            onChangeComplete={c => {
                              const safe = getSafeColorForElement(c.hex, theme);
                              const wasAdjusted = safe !== c.hex;
                              setBrandColorAdjusted(wasAdjusted);
                              setBrandColor(safe);
                              setValue('brand_color', safe, {
                                shouldDirty: true
                              });
                              if (wasAdjusted) {
                                toast.success(
                                  t('color-adjusted-for-better-readability') ||
                                    'Color automatically adjusted for better readability'
                                );
                              }
                            }}
                          />
                        </div>

                        {errors?.brand_color && (
                          <p className="mt-2 text-xs text-red-600">
                            {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                            {errors.brand_color.message as any}
                          </p>
                        )}
                      </div>
                    </Popover.Panel>
                  </Transition>
                </Popover>
              )}
            />
          </div>
        </div>
      </div>

      {/* Live Preview */}
      <div
        className="rounded-2xl border shadow-card p-4 sm:p-6"
        style={cardStyle}
      >
        <div className="flex items-center gap-2 mb-4">
          <Eye className="h-4 w-4" style={{ color: getColour('icon') }} />
          <h4 className="font-medium text-sm">{t('live-preview')}</h4>
        </div>

        <div
          className="rounded-2xl p-4 border"
          style={{
            backgroundColor: getColour('thirdLayer'),
            borderColor: getColour('hairline')
          }}
        >
          <div className="flex items-center gap-4 mb-4">
            <div
              className="w-10 h-10 rounded-2xl flex items-center justify-center overflow-hidden"
              style={{ backgroundColor: brandColor || getColour('thirdLayer') }}
            >
              {customer?.logo || logoFile ? (
                <img
                  src={
                    logoFile
                      ? URL.createObjectURL(logoFile)
                      : customer?.logo || ''
                  }
                  alt="Preview"
                  className="w-full h-full object-contain"
                />
              ) : (
                <span
                  className="text-sm font-bold"
                  style={{
                    // eslint-disable-next-line no-restricted-syntax
                    color: brandColor ? '#ffffff' : getColour('inactiveText')
                  }}
                >
                  {customer?.name?.charAt(0) || 'O'}
                </span>
              )}
            </div>
            <div>
              <p className="text-sm font-semibold">
                {customer?.name || 'Organization Name'}
              </p>
              <p className="text-xs opacity-60">
                {customer?.email || 'email@example.com'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              className="px-3 py-1.5 rounded-2xl text-xs font-medium text-white transition-opacity hover:opacity-90"
              style={{ backgroundColor: brandColor || getColour('activeText') }}
            >
              {t('sample-button')}
            </button>
            <span
              className="text-xs font-medium"
              style={{ color: brandColor || getColour('activeText') }}
            >
              {t('sample-link')}
            </span>
          </div>
        </div>
      </div>

      {deleteLogoIsOpen && (
        <ConfirmationModal
          isOpen={deleteLogoIsOpen}
          onClose={() => setDeleteLogoIsOpen(false)}
          onConfirm={handleDeleteLogo}
          size="small"
          title={t('delete-logo-confirmation')}
          caption={t('are-you-sure-you-want-to-delete-this-logo')}
          confirmLabel={isDeleting ? t('deleting') : t('delete')}
          cancelLabel={t('cancel')}
          confirmDisabled={isDeleting}
          destructive
          autoFocusConfirm
        />
      )}
    </div>
  );
}
